import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CreditCard } from "lucide-react";
import { useLocale, useTranslations } from "use-intl";

import { Skeleton } from "@lawline/ui/components/skeleton";

import { ExternalLink } from "@platform/components/ui/external-link";
import { Notification } from "@platform/components/ui/notification";
import { docsLink } from "@platform/core/docs";
import { billingQueryOptions } from "@platform/features/billing/billing.queries";
import { apiKeysQueryOptions } from "./api-keys.queries";
import { ApiKeysTable } from "./components/api-keys-table";
import { CreateApiKey } from "./components/create-api-key";

export function ApiKeysPage() {
	const t = useTranslations("apiKeys");
	const locale = useLocale();
	const { data: keys, isPending, error } = useQuery(apiKeysQueryOptions);
	const { data: billing } = useQuery(billingQueryOptions());

	// A revoked key is dead weight in the list, so the page hides it.
	const visible = keys?.filter((key) => key.status !== "revoked") ?? [];

	return (
		<section className="mx-auto mt-2 flex w-full max-w-3xl flex-col gap-6">
			<div className="flex items-center justify-between">
				<h1 className="text-2xl font-semibold">{t("title")}</h1>
				{billing && billing.billingStatus !== "active" && (
					<Notification variant="destructive">
						<CreditCard />
						<span>{t("billingInactive")}</span>
					</Notification>
				)}

				<CreateApiKey />
			</div>

			<div className="text-muted-foreground flex flex-col gap-1 text-sm">
				<p>
					{t.rich("docsIntro", {
						guide: (chunks) => (
							<ExternalLink href={docsLink("gettingStarted", locale)}>{chunks}</ExternalLink>
						),
						mcp: (chunks) => <ExternalLink href={docsLink("mcp", locale)}>{chunks}</ExternalLink>,
					})}
				</p>
				<p>
					{t.rich("aiAppsHint", {
						link: (chunks) => (
							<Link to="/ai-apps" className="text-foreground underline underline-offset-2">
								{chunks}
							</Link>
						),
					})}
				</p>
			</div>

			{isPending ? (
				<Skeleton className="h-24 w-full" />
			) : error ? (
				<p className="text-destructive text-sm">{t("error")}</p>
			) : (
				<ApiKeysTable apiKeys={visible} />
			)}
		</section>
	);
}
